import { SigninSilentArgs, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts'
import { IResultAuth } from './type'
import { Lock } from './helper/Lock'
import { LocalStorageSecure } from './helper/LocalStorageSecure'
import { ApplicationPaths, QueryParameterNames } from './constants'

const ApplicationName = () => (window as any).env.REACT_APP_NAME

interface ICallbackAuth {
  callback: () => void
  subscription: number
}

export class AuthorizeService {
  _callbacks: ICallbackAuth[] = []
  _nextSubscriptionId = 0
  _user?: User | null | void
  _isAuthenticated = false
  userManager?: UserManager

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  _popUpDisabled = true
  constructor() {
    this._lockEnsureUserManager = new Lock()
    this._lockSignIn = new Lock()
  }
  async isAuthenticated() {
    const user = await this.getUser()
    return !!user
  }

  async getUser() {
    if (this._user && this._user.profile) {
      return this._user.profile
    }
    await this.ensureUserManagerInitialized()
    try {
      const user = await this.userManager?.getUser()
      return user && user.profile
    } catch (error) {
      return null
    }
  }

  async getRoles() {
    const user = await this.getUser()
    if (await this.isAuthenticated()) {
      const roles = (user as any).role as string[]
      return roles ? (Array.isArray(roles) ? roles : [roles]) : []
    }
  }
  async getAccessToken() {
    await this.ensureUserManagerInitialized()
    const user = await this.userManager?.getUser()
    return user && user.access_token
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  _lockSignIn: Lock
  async signIn(state: any): Promise<IResultAuth> {
    return await this._lockSignIn.Once(async () => {
      await this.ensureUserManagerInitialized()
      try {
        const silentUser = await this.userManager?.signinSilent(this.createArguments(state))
        this.updateState(silentUser)
        return this.success(state)
      } catch (silentError) {
        // User might not be authenticated, fallback to popup authentication
        console.log('Silent authentication error: ', silentError)
        try {
          if (this._popUpDisabled) {
            throw new Error("Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.")
          }

          const popUpUser = await this.userManager?.signinPopup(this.createArguments())
          this.updateState(popUpUser)
          return this.success(state)
        } catch (popUpError: any) {
          if (popUpError.message === 'Popup window closed') {
            // The user explicitly cancelled the login action by closing an opened popup.
            return this.error('The user closed the window.')
          } else if (!this._popUpDisabled) {
            console.log('Popup authentication error: ', popUpError)
          }

          // PopUps might be blocked by the user, fallback to redirect
          try {
            await this.userManager?.signinRedirect(this.createArguments(state))
            return this.redirect()
          } catch (redirectError: any) {
            console.log('Redirect authentication error: ', redirectError)
            return this.error(redirectError)
          }
        }
      }
    })
  }
  getReturnUrl(state?: any) {
    const params = new URLSearchParams(window.location.search)
    const fromQuery = params.get(QueryParameterNames.ReturnUrl)
    if (fromQuery && !fromQuery.startsWith(`${ApplicationPaths.Origin()}/`)) {
      // This is an extra check to prevent open redirects.
      throw new Error('Invalid return url. The return url needs to have the same origin as the current page.')
    }
    return (state && state.returnUrl) || fromQuery || `${ApplicationPaths.Origin()}/`
  }
  async completeSignIn(url: string): Promise<IResultAuth> {
    try {
      await this.ensureUserManagerInitialized()
      const user = await this.userManager?.signinCallback(url)
      return this.success(user && user.state)
    } catch (error) {
      console.log('There was an error signing in: ', error)
      return this.error('There was an error signing in.')
    }
  }

  // We try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state: any): Promise<IResultAuth> {
    await this.ensureUserManagerInitialized()
    try {
      if (this._popUpDisabled) {
        throw new Error("Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.")
      }

      await this.userManager?.signoutPopup(this.createArguments())
      this.updateState(undefined)
      return this.success(state)
    } catch (popupSignOutError: any) {
      console.log('Popup signout error: ', popupSignOutError)
      try {
        await this.userManager?.signoutRedirect(this.createArguments(state))
        return this.redirect()
      } catch (redirectSignOutError: any) {
        console.log('Redirect signout error: ', redirectSignOutError)
        return this.error(redirectSignOutError)
      }
    }
  }
  //todo ::
  async completeSignOut(url: string): Promise<IResultAuth> {
    await this.ensureUserManagerInitialized()
    try {
      const response = await this.userManager?.signoutCallback(url)
      this.updateState()
      return this.success(response && (response as any).data)
    } catch (error: any) {
      console.log(`There was an error trying to log out '${error}'.`)
      return this.error(error)
    }
  }

  updateState(user?: User | null | void) {
    this._user = user
    this._isAuthenticated = !!this._user
    this.notifySubscribers()
  }

  subscribe(callback: () => void) {
    this._callbacks.push({
      callback,
      subscription: this._nextSubscriptionId++,
    })
    return this._nextSubscriptionId - 1
  }

  unsubscribe(subscriptionId: number) {
    const subscriptionIndex = this._callbacks
      .map((element, index) => (element.subscription === subscriptionId ? { found: true, index } : { found: false }))
      .filter((element) => element.found === true)
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`)
    }
    subscriptionIndex[0].index && this._callbacks.splice(subscriptionIndex[0].index, 1)
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback
      callback()
    }
  }

  createArguments(state?: any) {
    return { useReplaceToNavigate: true, data: state } as SigninSilentArgs
  }

  error(message: string) {
    return { status: AuthenticationResultStatus.Fail, message }
  }

  success(state: any) {
    return { status: AuthenticationResultStatus.Success, state }
  }

  redirect() {
    return { status: AuthenticationResultStatus.Redirect }
  }
  _lockEnsureUserManager: Lock
  async ensureUserManagerInitialized() {
    if (this.userManager !== undefined) {
      return
    }
    await this._lockEnsureUserManager.Once(async () => {
      const response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl()).catch((err) => {
        console.log(err)
        throw new Error()
      })
      if (!response.ok) {
        throw new Error(`Could not load settings for '${ApplicationName()}'`)
      }

      const settings: UserManagerSettings = await response.json()
      settings.automaticSilentRenew = true
      settings.includeIdTokenInSilentRenew = true
      settings.loadUserInfo = true
      settings.userStore = new WebStorageStateStore({
        prefix: ApplicationName(),
        store: new LocalStorageSecure((window as any).env.REACT_APP_USER_SECRET ?? ''),
      })

      settings.stateStore = new WebStorageStateStore({
        prefix: 'oidc.',
        store: new LocalStorageSecure((window as any).env.REACT_APP_STATE_SECRET ?? ''),
      })

      this.userManager = new UserManager(settings)
      this.userManager?.events.addUserSignedOut(async () => {
        await this.userManager?.removeUser()
        this.updateState(undefined)
      })
    })
  }

  static get instance() {
    return authService
  }
}

export const authService = new AuthorizeService()

export const AuthenticationResultStatus = {
  Redirect: 'redirect',
  Success: 'success',
  Fail: 'fail',
}
